<template>
    <div class="report-group-builder mx-2">
        <div class="report-group-builder-overview pb-5">
            <h1>{{ local_group.id ? 'Update' : 'Add' }} Report Group</h1>
            
            <div class="card mt-3">
                <div class="card-body">
                    <div class="row">
                        <div class="col-md-6 col-12 mb-3">
                            <label class="form-label">Group Name <span class="text-danger">*</span></label>
                            <input 
                                type="text" 
                                id="group-name-input" 
                                class="form-control" 
                                v-model="local_group.group_name"
                                :class="errors.group_name ? 'is-invalid' : ''" 
                                @change="clearErrorForField('group_name')"
                             aria-label="Text input">
                            <div class="text-danger invalid-feedback" v-if="errors.group_name">
                                {{ errors.group_name }}
                            </div>
                        </div>

                        <div class="col-md-6 col-12 mb-3">
                            <label class="form-label">Order <span class="text-danger">*</span></label>
                            <input 
                                type="number" 
                                id="order-input" 
                                class="form-control" 
                                v-model.number="local_group.order"
                                step="0.01"
                                min="0"
                                placeholder="e.g. 1, 2.5"
                                :class="errors.order ? 'is-invalid' : ''" 
                                @change="clearErrorForField('order')"
                             aria-label="Number">
                            <small class="text-muted">Whole numbers (1, 2) or decimals (e.g. 2.5) allowed.</small>
                            <div class="text-danger invalid-feedback" v-if="errors.order">
                                {{ errors.order }}
                            </div>
                        </div>

                        <div class="col-md-4 col-12 mb-3">
                            <label class="form-label">Icon</label>
                            <div class="d-flex align-items-center gap-2">
                                <div v-if="iconPreviewSrc" class="icon-preview-wrap flex-shrink-0 d-flex align-items-center gap-1">
                                    <img
                                        v-if="!iconImageBroken"
                                        :src="iconPreviewSrc"
                                        alt="Icon preview"
                                        class="icon icon-preview"
                                        @error="iconImageBroken = true"
                                    >
                                    <span v-else class="icon-broken" title="Icon failed to load">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                            <rect x="3" y="3" width="18" height="18" rx="2"/>
                                            <path d="M3 15l4-4 4 4 5-5 3 3"/>
                                            <path d="M10 8v.01"/>
                                        </svg>
                                        <span class="icon-broken-text">Broken</span>
                                    </span>
                                </div>
                                <div class="flex-grow-1">
                                    <input 
                                        type="text" 
                                        id="icon-input" 
                                        class="form-control" 
                                        v-model="local_group.icon"
                                        :class="errors.icon ? 'is-invalid' : ''" 
                                        @input="onIconInput"
                                     aria-label="Text input">
                                    <small class="text-muted">Icon name or path</small>
                                    <div class="text-danger invalid-feedback" v-if="errors.icon">
                                        {{ errors.icon }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="col-md-4 col-12 mb-3">
                            <label class="form-label">Color</label>
                            <select 
                                id="color-input" 
                                class="form-select" 
                                v-model="local_group.color"
                                :class="errors.color ? 'is-invalid' : ''" 
                                @change="clearErrorForField('color')"
                             aria-label="Color">
                                <option value="">Select a color...</option>
                                <option value="blue">Blue</option>
                                <option value="green">Green</option>
                                <option value="yellow">Yellow</option>
                                <option value="purple">Red</option>
                            </select>
                            <div class="text-danger invalid-feedback" v-if="errors.color">
                                {{ errors.color }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row mt-3">
                <div class="col-12 navbar navbar-expand">
                    <button 
                        type="submit" 
                        class="btn btn-primary ms-2" 
                        id="save-button" 
                        @click.prevent="save" 
                        :disabled="form.busy"
                    >
                        <img class="icon" src="/img/icons/avid-icons/save.svg" alt=""> Save 
                    </button>
                    <a href="/admin/report_group_builder" class="btn btn-secondary ms-2">
                        <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> Cancel
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Swal from 'sweetalert2';

export default {
    name: 'ReportGroupList',
    props: {
        reportGroup: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            local_group: {
                id: null,
                group_name: '',
                order: 0.0,
                icon: '',
                color: ''
            },
            errors: {
                group_name: '',
                order: '',
                icon: '',
                color: ''
            },
            form: {
                successful: false,
                busy: false,
                error: false 
            },
            iconImageBroken: false
        };
    },
    computed: {
        iconPreviewSrc() {
            const icon = this.local_group?.icon;
            if (!icon || !icon.trim()) return null;
            if (icon.startsWith('/')) return icon;
            return '/img/icons/avid-icons/' + icon;
        }
    },
    beforeMount() {
        if (this.reportGroup && this.reportGroup.id) {
            this.local_group = { ...this.reportGroup };
        } else {
            // Set default order for new group
            this.local_group.order = 1.0;
        }
        this.iconImageBroken = false;
    },
    methods: {
        clearErrors() {
            this.errors = {
                group_name: '',
                order: '',
                icon: '',
                color: ''
            };
        },
        clearErrorForField(field_name) {
            this.errors[field_name] = '';
        },
        onIconInput() {
            this.clearErrorForField('icon');
            this.iconImageBroken = false;
        },
        hasErrors() {
            this.clearErrors();
            
            if (!this.local_group.group_name) {
                this.errors.group_name = "Group name is required.";
            }
            
            const order = this.local_group.order;
            if (order === null || order === undefined || order === '') {
                this.errors.order = "Order is required.";
            } else if (typeof order !== 'number' || Number.isNaN(order)) {
                this.errors.order = "Order must be a number.";
            }
            
            return Object.values(this.errors).some(error => error !== "");
        },
        save() {
            if (this.hasErrors()) {
                Swal.fire({
                    title: "Error Saving Report Group",
                    text: "There were some issues saving this report group. Please review and make the suggested changes.",
                    icon: "error",
                    confirmButtonText: "View Errors",
                    customClass: {
                        popup: 'swal-error'
                    },
                });
                return;
            }

            this.form.busy = true;

            const order = Number(this.local_group.order);
            const payload = {
                group_name: this.local_group.group_name,
                order: Number.isFinite(order) ? order : this.local_group.order,
                icon: this.local_group.icon || null,
                color: this.local_group.color || null
            };

            if (this.local_group.id) {
                // Update existing Report Group
                window.axios.put(`/api/admin/report_group/${this.local_group.id}`, payload)
                    .then(response => {
                        this.local_group = response.data.data;
                        this.form.busy = false;
                        Swal.fire({
                            icon: 'success',
                            title: 'Success',
                            text: "The report group has been updated successfully!",
                            confirmButtonText: 'OK',
                            customClass: {
                                popup: 'swal-success'
                            },
                        }).then(() => {
                            window.location.href = '/admin/report_group_builder';
                        });
                    })
                    .catch(error => {
                        this.form.busy = false;
                        if (error.response && error.response.status === 422) {
                            const errorData = error.response.data.errors || {};
                            Object.keys(errorData).forEach(key => {
                                this.errors[key] = Array.isArray(errorData[key]) ? errorData[key][0] : errorData[key];
                            });
                            Swal.fire({
                                title: "Validation Error",
                                text: "Please correct the highlighted errors and try again.",
                                icon: "error",
                                confirmButtonText: "OK",
                                customClass: {
                                    popup: 'swal-error'
                                },
                            });
                        } else {
                            this.form.error = true;
                            Swal.fire({
                                title: "Error",
                                text: error.response?.data?.message || "An unexpected error occurred while updating the report group.",
                                icon: "error",
                                confirmButtonText: "OK",
                                customClass: {
                                    popup: 'swal-error'
                                },
                            });
                        }
                    });
            } else {
                // Create a new Report Group
                window.axios.post('/api/admin/report_group', payload)
                    .then(response => {
                        this.local_group = response.data.data;
                        this.form.busy = false;
                        Swal.fire({
                            icon: 'success',
                            title: 'Success',
                            text: "The report group has been created successfully!",
                            confirmButtonText: 'OK',
                            customClass: {
                                popup: 'swal-success'
                            },
                        }).then(() => {
                            window.location.href = '/admin/report_group_builder';
                        });
                    })
                    .catch(error => {
                        this.form.busy = false;
                        if (error.response && error.response.status === 422) {
                            const errorData = error.response.data.errors || {};
                            Object.keys(errorData).forEach(key => {
                                this.errors[key] = Array.isArray(errorData[key]) ? errorData[key][0] : errorData[key];
                            });
                            Swal.fire({
                                title: "Validation Error",
                                text: "Please correct the highlighted errors and try again.",
                                icon: "error",
                                confirmButtonText: "OK",
                                customClass: {
                                    popup: 'swal-error'
                                },
                            });
                        } else {
                            this.form.error = true;
                            Swal.fire({
                                title: "Error",
                                text: error.response?.data?.message || "An unexpected error occurred while creating the report group.",
                                icon: "error",
                                confirmButtonText: "OK",
                                customClass: {
                                    popup: 'swal-error'
                                },
                            });
                        }
                    });
            }
        }
    }
};
</script>

<style scoped>
.icon-broken {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    color: #dc3545;
}
.icon-broken svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
}
.icon-broken-text {
    font-size: 0.875rem;
    font-weight: 500;
}
</style>

